import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AdminShell from "@/components/admin/AdminShell";
import api, { formatApiError, API } from "@/lib/api";
import VideoEmbed from "@/components/app/VideoEmbed";
import { CheckCircle2, Circle, Download, ArrowLeft, Save, Eye, ChevronDown, ChevronRight, ClipboardPaste } from "lucide-react";

/** One row in the checklist — Kajabi URL or full <iframe> for a single video slot. */
function SlotRow({ slot, dirty, onChange }) {
  const [preview, setPreview] = useState(false);
  const has = !!(dirty ?? slot.current_value);
  return (
    <div className="rounded-xl bg-[#0F1020] border border-white/10 p-3.5" data-testid={`bulk-row-${slot.content_type}-${slot.key}`}>
      <div className="flex items-start gap-3">
        <div className="mt-0.5 shrink-0">
          {has ? <CheckCircle2 size={16} className="text-emerald-300" /> : <Circle size={16} className="text-slate-500" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-white">{slot.label}</div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mt-0.5">{slot.content_type} · {slot.key}</div>
          <textarea
            data-testid={`bulk-input-${slot.content_type}-${slot.key}`}
            value={dirty ?? slot.current_value ?? ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Paste Kajabi URL or full <iframe ...></iframe> embed"
            rows={2}
            className="mt-2 w-full px-3 py-2 rounded-lg bg-[#151737] border border-white/10 focus:border-amber-300 focus:outline-none text-slate-100 text-xs font-mono"
          />
          {(dirty || slot.current_value) && (
            <button type="button" onClick={() => setPreview((p) => !p)} data-testid={`bulk-preview-${slot.content_type}-${slot.key}`} className="mt-1.5 inline-flex items-center gap-1 text-[11px] text-slate-400 hover:text-amber-300">
              <Eye size={11} /> {preview ? "Hide preview" : "Preview embed"}
            </button>
          )}
          {preview && (
            <div className="mt-2 max-w-md"><VideoEmbed value={dirty ?? slot.current_value} title={slot.label} testid={`bulk-preview-embed-${slot.content_type}-${slot.key}`} /></div>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({ title, count, filled, defaultOpen = true, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-2xl bg-[#151737] border border-white/10">
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between px-5 py-3.5">
        <div className="flex items-center gap-3">
          {open ? <ChevronDown size={16} className="text-slate-400" /> : <ChevronRight size={16} className="text-slate-400" />}
          <div className="font-display text-base font-bold text-white">{title}</div>
          <span className="text-[11px] font-semibold text-slate-300 bg-white/10 px-2 py-0.5 rounded-full">{filled}/{count} filled</span>
        </div>
      </button>
      {open && <div className="px-5 pb-5 space-y-2">{children}</div>}
    </div>
  );
}

export default function AdminBulkVideos() {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dirty, setDirty] = useState({}); // { "content_type|key": value }
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/content/video-slots");
      setSlots(data.slots);
      setDirty({});
    } catch (e) { setErr(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const grouped = useMemo(() => {
    const g = {};
    for (const s of slots) {
      (g[s.group] = g[s.group] || []).push(s);
    }
    return g;
  }, [slots]);

  const dirtyCount = Object.keys(dirty).length;

  const setDirtyValue = (slot, value) => {
    const k = `${slot.content_type}|${slot.key}|${slot.field}`;
    // If new value equals current_value, drop from dirty (nothing to save).
    setDirty((d) => {
      const next = { ...d };
      if (value === (slot.current_value || "")) delete next[k];
      else next[k] = value;
      return next;
    });
  };

  const saveAll = async () => {
    if (!dirtyCount) return;
    setSaving(true); setMsg(""); setErr("");
    try {
      const items = Object.entries(dirty).map(([k, value]) => {
        const [content_type, key, field] = k.split("|");
        return { content_type, key, field: field || "video_url", value };
      });
      const { data } = await api.post("/admin/content/bulk", { items });
      const parts = [];
      if (data.saved) parts.push(`${data.saved} saved`);
      if (data.cleared) parts.push(`${data.cleared} cleared`);
      if (data.errors?.length) parts.push(`${data.errors.length} error(s)`);
      setMsg(parts.join(" · ") || "No changes");
      await load();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setSaving(false); setTimeout(() => setMsg(""), 3500); }
  };

  const downloadChecklist = async () => {
    try {
      const token = localStorage.getItem("btt_token");
      const res = await fetch(`${API}/admin/content/video-slots.csv`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "breathe-thrive-video-checklist.csv";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { alert(e.message); }
  };

  // Parse a pasted CSV (or TSV) using content_type + key columns to fill the corresponding rows.
  const importPastedCsv = () => {
    setErr(""); setMsg("");
    try {
      const lines = pasteText.split(/\r?\n/).filter((l) => l.trim().length > 0);
      if (lines.length < 2) throw new Error("Paste needs a header row plus at least one data row.");
      const splitLine = (line) => {
        const out = []; let cur = ""; let inQ = false;
        for (let i = 0; i < line.length; i++) {
          const ch = line[i];
          if (inQ) {
            if (ch === '"' && line[i + 1] === '"') { cur += '"'; i++; }
            else if (ch === '"') inQ = false;
            else cur += ch;
          } else {
            if (ch === '"') inQ = true;
            else if (ch === "," || ch === "\t") { out.push(cur); cur = ""; }
            else cur += ch;
          }
        }
        out.push(cur);
        return out;
      };
      const header = splitLine(lines[0]).map((h) => h.trim().toLowerCase());
      const idx = (name) => header.indexOf(name);
      const ctI = idx("content_type"), kI = idx("key"), vI = idx("value") !== -1 ? idx("value") : idx("current_value");
      if (ctI < 0 || kI < 0 || vI < 0) throw new Error("CSV must include content_type, key, and value (or current_value) columns.");
      const nextDirty = { ...dirty };
      let applied = 0;
      for (let i = 1; i < lines.length; i++) {
        const cols = splitLine(lines[i]);
        const ct = (cols[ctI] || "").trim();
        const key = (cols[kI] || "").trim();
        const val = cols[vI] ?? "";
        if (!ct || !key) continue;
        // Only apply if the slot exists.
        const slot = slots.find((s) => s.content_type === ct && String(s.key) === key);
        if (!slot) continue;
        if (val === (slot.current_value || "")) continue; // no-op
        nextDirty[`${ct}|${key}|${slot.field}`] = val;
        applied++;
      }
      setDirty(nextDirty);
      setPasteOpen(false);
      setPasteText("");
      setMsg(`${applied} row(s) staged from paste — review, then Save all`);
      setTimeout(() => setMsg(""), 5000);
    } catch (e) { setErr(e.message); }
  };

  return (
    <AdminShell subtitle="Content management" title="Bulk video import"
      right={<Link to="/admin/content" data-testid="bulk-back" className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-white"><ArrowLeft size={14} /> Back to CMS</Link>}
    >
      <div className="rounded-2xl bg-[#151737] border border-white/10 p-5 mb-4">
        <div className="grid md:grid-cols-3 gap-4 items-center">
          <div className="md:col-span-2">
            <div className="text-sm text-slate-200 leading-relaxed">
              This is your <b className="text-white">Kajabi gathering checklist</b>. Every video slot in the parent app is listed below.
              For each one, paste the <b>Kajabi share URL</b> (e.g. <code className="text-amber-300 text-xs">https://...kajabi.com/...</code>) or the full <code className="text-amber-300 text-xs">{"<iframe>"}</code> embed code Kajabi gives you.
              Rows already filled show a green check; missing ones are grey.
            </div>
            <div className="mt-3 flex items-center gap-3 text-xs text-slate-300 flex-wrap">
              <span className="inline-flex items-center gap-1"><CheckCircle2 size={12} className="text-emerald-300" /> {slots.filter((s) => (dirty[`${s.content_type}|${s.key}|${s.field}`] ?? s.current_value)).length} filled</span>
              <span className="inline-flex items-center gap-1"><Circle size={12} className="text-slate-500" /> {slots.length - slots.filter((s) => (dirty[`${s.content_type}|${s.key}|${s.field}`] ?? s.current_value)).length} missing</span>
              <span className="text-slate-500">·</span>
              <span>{slots.length} slots total</span>
              {dirtyCount > 0 && <span className="ml-1 px-2 py-0.5 rounded-full bg-amber-400/15 text-amber-300 font-semibold">{dirtyCount} unsaved change{dirtyCount === 1 ? "" : "s"}</span>}
            </div>
          </div>
          <div className="flex md:justify-end gap-2 flex-wrap">
            <button type="button" onClick={downloadChecklist} data-testid="bulk-download-csv" className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-200 bg-white/10 hover:bg-white/20 px-3 py-2 rounded-full">
              <Download size={12} /> Download checklist CSV
            </button>
            <button type="button" onClick={() => setPasteOpen(true)} data-testid="bulk-paste-csv" className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-200 bg-white/10 hover:bg-white/20 px-3 py-2 rounded-full">
              <ClipboardPaste size={12} /> Paste CSV
            </button>
            <button type="button" onClick={saveAll} disabled={!dirtyCount || saving} data-testid="bulk-save-all" className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#0F1020] bg-gradient-to-r from-amber-400 to-rose-400 px-4 py-2 rounded-full hover:opacity-90 disabled:opacity-40">
              <Save size={12} /> {saving ? "Saving…" : `Save all (${dirtyCount})`}
            </button>
          </div>
        </div>
        {msg && <div className="mt-3 text-xs text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded px-3 py-2" data-testid="bulk-msg">{msg}</div>}
        {err && <div className="mt-3 text-xs text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded px-3 py-2" data-testid="bulk-err">{err}</div>}
      </div>

      {pasteOpen && (
        <div className="rounded-2xl bg-[#151737] border border-amber-300/40 p-5 mb-4" data-testid="bulk-paste-dialog">
          <div className="text-sm font-semibold text-white mb-2">Paste a CSV or TSV</div>
          <div className="text-xs text-slate-400 mb-3">
            Header row must include <code className="text-amber-300">content_type</code>, <code className="text-amber-300">key</code>, and <code className="text-amber-300">value</code> (or <code className="text-amber-300">current_value</code>). The easiest workflow: click <b>Download checklist CSV</b>, fill the <code className="text-amber-300">current_value</code> column in Google Sheets / Excel, then paste it back here.
          </div>
          <textarea value={pasteText} onChange={(e) => setPasteText(e.target.value)} data-testid="bulk-paste-text" rows={8} placeholder="Paste CSV contents here…" className="w-full px-3 py-2 rounded-lg bg-[#0F1020] border border-white/10 focus:border-amber-300 focus:outline-none text-slate-100 text-xs font-mono" />
          <div className="mt-3 flex justify-end gap-2">
            <button type="button" onClick={() => { setPasteOpen(false); setPasteText(""); }} className="text-xs text-slate-400 hover:text-white px-3 py-1.5">Cancel</button>
            <button type="button" onClick={importPastedCsv} data-testid="bulk-paste-apply" className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#0F1020] bg-gradient-to-r from-amber-400 to-rose-400 px-4 py-1.5 rounded-full hover:opacity-90">Stage changes</button>
          </div>
        </div>
      )}

      {loading && <div className="text-slate-400 text-sm">Loading checklist…</div>}
      {!loading && (
        <div className="space-y-3">
          {Object.entries(grouped).map(([group, rows]) => {
            const count = rows.length;
            const filled = rows.filter((r) => (dirty[`${r.content_type}|${r.key}|${r.field}`] ?? r.current_value)).length;
            return (
              <Section key={group} title={group} count={count} filled={filled} defaultOpen={group.startsWith("Tribe") || group.startsWith("Education") || group === "Session Start-Here videos" || group === "BREATHE Framework"}>
                {rows.map((s) => (
                  <SlotRow
                    key={`${s.content_type}|${s.key}|${s.field}`}
                    slot={s}
                    dirty={dirty[`${s.content_type}|${s.key}|${s.field}`]}
                    onChange={(v) => setDirtyValue(s, v)}
                  />
                ))}
              </Section>
            );
          })}
        </div>
      )}
    </AdminShell>
  );
}
